import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx, hasWidthClass } from './cx.ts';

export interface SelectOption {
  readonly value: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
}

interface Props {
  readonly value: string | undefined;
  readonly onValueChange: (value: string) => void;
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
  readonly mono?: boolean;
  readonly className?: string;
  readonly testId?: string;
}

/** Field Notes select: the design-system replacement for native OS chrome. */
export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  disabled = false,
  ariaLabel,
  mono = false,
  className,
  testId,
}: Props): JSX.Element {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        data-testid={testId}
        className={cx(
          'inline-flex h-9 items-center justify-between gap-2 rounded border border-line bg-well px-3 text-body text-ink transition-colors duration-150 hover:border-line-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 data-[state=open]:border-forest data-[placeholder]:text-ink-faint disabled:pointer-events-none disabled:opacity-60',
          !hasWidthClass(className) && 'w-full',
          mono && 'font-mono',
          className,
        )}
      >
        <span className="truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 text-ink-low" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-[320px] min-w-[var(--radix-select-trigger-width)] overflow-y-auto rounded-lg border border-line bg-raised p-1 shadow-raised"
        >
          <SelectPrimitive.Viewport>
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cx(
                  'flex cursor-default select-none items-center justify-between gap-3 rounded px-2.5 py-1.5 text-body text-ink-mid outline-none transition-colors duration-150 data-[highlighted]:bg-active data-[highlighted]:text-ink data-[disabled]:opacity-50 data-[state=checked]:text-forest',
                  mono && 'font-mono',
                )}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator>
                  <Check aria-hidden="true" className="h-3.5 w-3.5" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
